import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Centered, Column, Input, Loader, Modal } from '../../../../../components'
import Button, { Sizes as ButtonSizes } from '../../../../../components/Button'
import Form, { FormGroup, FormHeader } from '../../../../../components/Form'
import Padding, { Sizes as PaddingSizes } from '../../../../../components/Padding'
import useDefaultValues from './useDefaultValues'
import useSelectedValues from './useSelectedValues'
import { useAppSelector } from '../../../../../reducers'
import useDisplayOptions from './useDisplayOptions'
import ContentButton from '../../ContentButton'
import { colors } from '../../../../../constants'
import { isEqual } from 'lodash'
import useChannelsFiltersParams from './useChannelsFiltersParams'
import { useContentURLParams } from '../../hooks'

const NotificationsBadge = styled.div`
    top: 8px;
    position: absolute;
    left: 55%;
    background: ${colors.orange};
    border-radius: 50%;
    height: 10px;
    width: 10px;
`

export default function ChannelsFilters() {
    const { t } = useTranslation()
    const [searchParams, setSearchParams, resetSearchParams] = useChannelsFiltersParams()
    const selectedValues = useSelectedValues()
    const defaultFormValues = useDefaultValues()
    const displayOptions = useDisplayOptions()
    const { searchChannel } = useContentURLParams()
    const isFetchingChains = useAppSelector((state) => state.chains.isWorking)

    const [isModalOpen, setIsModalOpen] = React.useState(false)

    const [isFetchingData] = React.useState<boolean>(false)

    /**
     * Effect for initializing selected values with data from the URL search string
     */
    React.useEffect(() => {
        const chains: number[] = searchParams.chains ?? defaultFormValues.chains

        selectedValues.setters.chains(chains)
    }, [defaultFormValues.chains, searchParams, selectedValues.setters])

    const onChangeChains = ({ target: { value } }: { target: { value: number[] } }) => {
        selectedValues.setters.chains(value)
    }

    const onDiscardChanges = () => {
        selectedValues.setters.chains(searchParams.chains ?? defaultFormValues.chains)
        setIsModalOpen(false)
    }

    /**
     * Callback for submitting form
     */
    const onSubmit = () => {
        setSearchParams(selectedValues.values)
        setIsModalOpen(false)
    }

    /**
     * Callback for resetting filters to default values
     */
    const onClickResetFilters = () => {
        resetSearchParams()

        // Closing form
        setIsModalOpen(false)
    }

    const areFiltersActive = !isEqual(
        { ...defaultFormValues, search: searchChannel },
        { chains: searchParams.chains, search: '' },
    )

    return (
        <>
            <ContentButton
                onClick={() => setIsModalOpen(true)}
                theme={areFiltersActive ? 'primary' : 'secondary'}
            >
                {areFiltersActive && <NotificationsBadge />}
                <FontAwesomeIcon icon="sliders-h" size="1x" />
            </ContentButton>
            <Modal isOpen={isModalOpen} onClose={onDiscardChanges}>
                <Form>
                    <FormHeader>
                        <Padding right bottom size={PaddingSizes.sm}>
                            <Column>{t('filters-device')}</Column>
                        </Padding>
                    </FormHeader>
                    <FormGroup title={t('Chains')}>
                        {isFetchingChains && <Centered horizontal><Loader /></Centered>}
                        {!isFetchingChains && (
                            <Input
                                id="selectedChains"
                                name="selectedChains"
                                type="multiple"
                                onChange={onChangeChains}
                                value={selectedValues.values.chains}
                                values={displayOptions.chains}
                                column
                            />
                        )}
                    </FormGroup>
                    <div className="modal-bottom">
                        <FormGroup>
                            <Column right width="100%">
                                <Button
                                    theme="secondary"
                                    onClick={() => setIsModalOpen(false)}
                                    size={ButtonSizes.sm}
                                >
                                    {t('Cancel')}
                                </Button>
                                <Button
                                    theme="lightDanger"
                                    onClick={onClickResetFilters}
                                    size={ButtonSizes.sm}
                                    radius="5"
                                >
                                    {t('Clear filters')}
                                </Button>
                                <Button
                                    onClick={onSubmit}
                                    size={ButtonSizes.sm}
                                    theme={isFetchingData ? 'loading' : 'forestGreen'}
                                    disabled={isFetchingData}
                                >
                                    {isFetchingData ? '...' : t('Filter')}
                                </Button>
                            </Column>
                        </FormGroup>
                    </div>
                </Form>
            </Modal>
        </>
    )
}